import AlertSvg from '@/../img/alert.svg';
import styled from 'styled-components';

export const IssueReportWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: 580px;
  padding: 20px 20px 2px 20px;
  padding-bottom: ${(props) => (props.free ? '20px' : '10px')};

  background-color: ${(props) =>
    props.free ? 'rgba(252, 255, 230, 0.5)' : 'rgba(255, 247, 230, 0.5)'};
  border-radius: 6px;
  border-style: solid;
  border-width: 1px;
  border-color: ${(props) => (props.free ? '#d3f261' : '#ffd666')};

  @media print {
    display: block !important;
    width: 500px !important;
  }
`;

export const StyledText = styled.div`
  font-size: 14px;
  height: 15px;
  padding-left: 2px;
  margin-top: ${(props) => props.mt || 'inherit'};
  line-height: 1.29;
  color: rgba(0, 0, 0, 0.85);
`;

const COMPLIANT = 'COMPLIANT';

const StyledViolationTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-size: 12px;
  font-weight: bold;
  line-height: 1.17;
  color: #101938;
  margin-top: 30px;
  height: 14px;
`;

const Spacer = styled.div`
  height: 14px;
  width: 8px;
`;

export const IconSizer = styled.div`
  width: 14px;
  height: 14px;
  display: inline-block;
  .platform-icon > path {
    fill: rgba(16, 25, 56, 0.65);
  }
  .platform-icon > g {
    fill: rgba(16, 25, 56, 0.65);
  }
`;

export const IssueSummaryTitle = styled.div`
  height: 14px;
  font-weight: 700;
  font-size: 14px;
  color: #101938;
`;

export const IssueSummaryReportContainer = styled(IssueReportWrapper)`
  padding-bottom: 20px;
  ${({ onClick }) => Boolean(onClick) && 'cursor: pointer;'};
  text-align: left;
`;

export const IssueSummaryReportContent = styled.ul`
  list-style: none;
  padding: 0;
  margin-top: 8.5px;
  margin-bottom: 0;
`;

export const IssueSummaryReportRow = styled.li`
  margin: 5.5px 0;
  text-align: left;
`;

// displayed when no violations are found
export function IssueFreeMessage({ children }) {
  return (
    <>
      <StyledViolationTitle title={COMPLIANT} />
      <StyledText mt="10px" css="height: unset;">
        {children}
      </StyledText>
    </>
  );
}

export function StyledViolationTitle({ title }) {
  if (title !== COMPLIANT) {
    return (
      <StyledViolationTitleWrapper>
        <IconSizer>
          <AlertSvg />
        </IconSizer>
        <Spacer />
        {title}
      </StyledViolationTitleWrapper>
    );
  }
  return <></>;
}

/**
 * Displays a report box that summarizes number of issues with
 * a property (e.g. complaints or violations) by issue type
 * (e.g. "Noise" for complaint type).
 * @param {string} props.className CSS classes to apply to report container element
 * Makes the component styleable via `styled-components`
 * @param {Map<string, number>} props.issueCountByType Map of issue types to number
 * of issues of the type to be summarized in the report
 * @param {string | JSX.Element} props.title Title heading at the top of the report
 * @param {string | JSX.Element} props.noIssuesMessage Content to display if no issues are
 * reported
 * @param {number} props.maxIssueRows Maximum number of issue types to display. If
 * provided data contains more than this number of issue types, the report will display
 * a `+ N more` line at the bottom, where N is the number of issues not covered by the summarized
 * issue types displayed above that line.
 * @param {function} props.issueTypeLabelFactory A function that takes an issue type
 * and returns a user-friendly string to be displayed for that issue type in the report
 * @param {function | undefined} onClick Handler for clicks on the report. Optional.
 * @returns {JSX.Element}
 * @constructor
 */
export function IssueSummaryReport(props) {
  const {
    className,
    issueCountByType,
    title,
    noIssuesMessage,
    maxIssueRows = 2,
    issueTypeLabelFactory = (type) => type,
    onClick,
  } = props;
  const issueTypes = Object.keys(issueCountByType);
  const totalIssueCount = Object.values(issueCountByType).reduce(
    (acc, count) => acc + count,
    0
  );

  const topIssueCounts = issueTypes
    .map((issueType) => ({ issueType, count: issueCountByType[issueType] }))
    .sort((issueCount1, issueCount2) => issueCount2.count - issueCount1.count);

  const hiddenIssueCount = topIssueCounts
    .slice(maxIssueRows)
    .reduce((acc, { count }) => acc + count, 0);

  return (
    <IssueSummaryReportContainer
      as={onClick && 'button'}
      className={className}
      free={!issueTypes.length}
      onClick={onClick}
    >
      <IssueSummaryTitle>{`${title} (${totalIssueCount})`}</IssueSummaryTitle>
      {!issueTypes.length ? (
        <IssueFreeMessage>{noIssuesMessage}</IssueFreeMessage>
      ) : (
        <>
          <IssueSummaryReportContent aria-label={title}>
            {topIssueCounts
              .slice(0, maxIssueRows)
              .map(({ issueType, count }) => (
                <IssueSummaryReportRow
                  aria-label={issueTypeLabelFactory(issueType)}
                  key={issueType}
                >
                  {`${issueTypeLabelFactory(issueType)} (${count})`}
                </IssueSummaryReportRow>
              ))}
          </IssueSummaryReportContent>
          {hiddenIssueCount > 0 && `+ ${hiddenIssueCount} more`}
        </>
      )}
    </IssueSummaryReportContainer>
  );
}
